import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';

export type CutRange = {
    begin: number;
    end: number;
};

export type AssetPayload = {
    provider: string;
    id: number;
};

export type ClipPayload = AssetPayload & { cutRange: CutRange };

export type ClipData = {
    state?: string;
    progress?: number;
    clipDownloadPending?: boolean;
    clips?: {
        [beginTimeToEndTime: string]: {
            isReady?: boolean;
            url?: string;
            error?: string;
        };
    };
};

export const CLIP_DOWNLOAD_STATE = {
    PROGRESS: 'convert',
    DONE: 'done',
    ERROR: 'error',
} as const;

const initialState: Record<string, Record<'clip', ClipData>> = {};

function getAssetKey(provider: string, assetId: number) {
    return `${provider}:${assetId}`;
}

function getClipDownloadKey(begin: number, end: number) {
    return `${begin}:${Math.floor(end)}`;
}

function getDefaultClipState(state: Draft<typeof initialState>, provider: string, assetId: number) {
    const key = getAssetKey(provider, assetId);

    if (!state[key]) {
        state[key] = { clip: { state: '', progress: 0, clipDownloadPending: false, clips: {} } };
    }

    const asset = state[key];

    if (!asset.clip) {
        asset.clip = { state: '', progress: 0, clipDownloadPending: false, clips: {} };
    }

    const { clip } = asset;

    if (!clip.clips) {
        clip.clips = {};
    }

    return clip;
}

function clipDownloadReducer(state: Draft<typeof initialState>, payload: ClipPayload) {
    const {
        id: assetId,
        provider,
        cutRange: { begin, end },
    } = payload;

    const clipKey = getClipDownloadKey(begin, end);

    const clip = getDefaultClipState(state, provider, assetId);

    clip.state = 'convert';

    clip.progress = 0;

    clip.clipDownloadPending = true;

    clip.clips[clipKey] = { url: undefined, isReady: false };
}

function clipDownloadUrlReducer(state: Draft<typeof initialState>, payload: ClipPayload & { url: string }) {
    const {
        id: assetId,
        provider,
        cutRange: { begin, end },
        url,
    } = payload;

    const clipKey = getClipDownloadKey(begin, end);

    const clip = getDefaultClipState(state, provider, assetId);

    clip.clips[clipKey] = {
        ...clip.clips[clipKey],
        url,
    };
}

function clipDownloadProgressReducer(
    state: Draft<typeof initialState>,
    payload: ClipPayload & { url: string; progress: number }
) {
    const {
        id: assetId,
        provider,
        cutRange: { begin, end },
        url,
        progress,
    } = payload;

    const clipKey = getClipDownloadKey(begin, end);

    const clip = getDefaultClipState(state, provider, assetId);

    clip.state = 'convert';

    clip.progress = Math.round(progress);

    clip.clipDownloadPending = false;

    clip.clips[clipKey] = { url, isReady: false };
}

function clipDownloadCompleteReducer(state: Draft<typeof initialState>, payload: ClipPayload & { url: string }) {
    const {
        id: assetId,
        provider,
        cutRange: { begin, end },
        url,
    } = payload;

    const clipKey = getClipDownloadKey(begin, end);

    const clip = getDefaultClipState(state, provider, assetId);

    clip.state = 'done';

    clip.progress = 100;

    clip.clipDownloadPending = false;

    clip.clips[clipKey] = { url, isReady: true };
}

function clipDownloadFailureReducer(state: Draft<typeof initialState>, payload: ClipPayload & { error: string }) {
    const {
        id: assetId,
        provider,
        cutRange: { begin, end },
        error,
    } = payload;

    const clipKey = getClipDownloadKey(begin, end);

    const clip = getDefaultClipState(state, provider, assetId);

    clip.state = 'error';

    clip.clipDownloadPending = false;

    clip.clips[clipKey] = {
        url: undefined,
        isReady: false,
        error,
    };
}

const clipDownloadSlice = createSlice({
    name: 'clipDownload',
    initialState,
    reducers: {
        clipDownload(state, action: PayloadAction<ClipPayload>) {
            clipDownloadReducer(state, action.payload);
        },
        clipDownloadUrl(state, action: PayloadAction<ClipPayload & { url: string }>) {
            clipDownloadUrlReducer(state, action.payload);
        },
        clipDownloadProgress(state, action: PayloadAction<ClipPayload & { url: string; progress: number }>) {
            clipDownloadProgressReducer(state, action.payload);
        },
        clipDownloadComplete(state, action: PayloadAction<ClipPayload & { url: string }>) {
            clipDownloadCompleteReducer(state, action.payload);
        },
        clipDownloadFailure(state, action: PayloadAction<ClipPayload & { error: string }>) {
            clipDownloadFailureReducer(state, action.payload);
        },
    },
});

export const { clipDownload, clipDownloadUrl, clipDownloadProgress, clipDownloadComplete, clipDownloadFailure } =
    clipDownloadSlice.actions;

export default clipDownloadSlice;
